import type { PrintMethodGuideItem } from '@repo/api-contracts';
import { type ReactElement, useEffect, useRef } from 'react';
import { usePrintMethodGuideQuery } from '@/entities/print-method';
import { useModalDialog } from '@/shared/lib';
import { Button, cn, DialogFrame } from '@/shared/ui';

interface PrintGuideDialogProps {
  /** 처음 열 때 강조하고 보이게 할 인쇄 방식 이름 — 누른 칩의 이름. */
  readonly focusName: string;
  /** 이 상품이 쓰는 인쇄 방식 이름 — 목록에서 "이 상품 적용" 표식을 단다. */
  readonly productMethodNames: readonly string[];
  readonly onClose: () => void;
}

/** 이 상품이 쓰는 방식을 앞에, 나머지는 이름순 그대로 — 고객이 고른 상품과 관련 있는 설명부터 읽게 한다. */
const orderForProduct = (
  items: readonly PrintMethodGuideItem[],
  productMethodNames: readonly string[],
): readonly PrintMethodGuideItem[] => {
  const applied = new Set(productMethodNames);
  return [
    ...items.filter((item) => applied.has(item.name)),
    ...items.filter((item) => !applied.has(item.name)),
  ];
};

/**
 * 프린트 가이드 — 상품 상세의 "인쇄 방식" 칩을 누르면 뜨는 모달. 전체 인쇄 방식을 이름·설명으로 늘어놓고,
 * 누른 방식은 강조해 화면에 보이게 하고 이 상품이 쓰는 방식에는 표식을 단다.
 * 설명은 운영자가 인쇄 방식 마스터에서 적는다 — 없으면 "설명 준비 중"으로 두고 지어내지 않는다.
 */
export const PrintGuideDialog = ({
  focusName,
  productMethodNames,
  onClose,
}: PrintGuideDialogProps): ReactElement => {
  const modal = useModalDialog(onClose, true);
  const guide = usePrintMethodGuideQuery({ enabled: true });
  const focusRef = useRef<HTMLLIElement | null>(null);
  const items = orderForProduct(guide.data ?? [], productMethodNames);
  const hasFocusItem = items.some((item) => item.name === focusName);

  // 목록이 채워진 뒤 누른 방식이 보이도록 스크롤한다 — 모달 본문이 길면 아래에 숨을 수 있다.
  useEffect(() => {
    // jsdom 에는 scrollIntoView 가 없다 — 있을 때만 부른다.
    if (hasFocusItem) focusRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [hasFocusItem]);

  return (
    <DialogFrame
      modal={modal}
      title="프린트 가이드"
      className="max-w-2xl"
      footer={
        <Button type="button" variant="outline" onClick={onClose}>
          닫기
        </Button>
      }
    >
      {guide.isPending ? (
        <p role="status" className="text-muted-foreground">
          인쇄 방식 설명을 불러오는 중입니다.
        </p>
      ) : null}
      {guide.isError ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-destructive">인쇄 방식 설명을 불러오지 못했습니다.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void guide.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      ) : null}
      {items.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-2">
          {items.map((item) => {
            const isFocused = item.name === focusName;
            const isApplied = productMethodNames.includes(item.name);
            return (
              <li
                key={item.id}
                ref={isFocused ? focusRef : undefined}
                aria-current={isFocused ? 'true' : undefined}
                className={cn(
                  'flex flex-col gap-1.5 border border-foreground/10 p-4',
                  isFocused && 'border-foreground bg-muted/40',
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-bold">{item.name}</h3>
                  {isApplied ? (
                    <span className="bg-brand-lime px-1.5 py-0.5 text-[11px] font-semibold text-black">
                      이 상품 적용
                    </span>
                  ) : null}
                </div>
                <p
                  className={cn(
                    'text-sm leading-relaxed whitespace-pre-line',
                    item.description === null && 'text-muted-foreground',
                  )}
                >
                  {item.description ?? '설명 준비 중입니다.'}
                </p>
              </li>
            );
          })}
        </ul>
      ) : null}
    </DialogFrame>
  );
};
